import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { isAfter } from 'date-fns';
import { Button, cn, Popover, useSnackbar } from '@c2fo/liquidity';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';
import emptystatements from '@/enterprise/assets/emptystatementsgrey.svg?url';
import {
  getCommonPinningStyles,
  rightShadowClassNames,
  usePinnedColumnShadow,
} from '@/enterprise/components/DataTable/tablePinning';
import DownloadMenu from '@/enterprise/components/DownloadMenu';
import { INSTANT_PAY_FUNDING_PARTNER } from '@/enterprise/components/InstantPay/instantPayFundingPartner';
import NoDataState from '@/enterprise/components/NoDataState';
import Skeleton from '@/enterprise/components/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableDisplay,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TableSortArrow,
  TableSortDirection,
} from '@/enterprise/components/Table';
import { downloadInstantPayStatement, InstantPayStatementType } from '@/enterprise/data/downloadInstantPayStatement';
import { downloadSupplierMarketClearCsv } from '@/enterprise/data/downloadSupplierMarketClearCsv';
import useCreditNoteByClear, { CreditNoteByClearParams } from '@/enterprise/data/useCreditNoteByClear';
import useFeature from '@/lib/features';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { dateAsIsoString } from '@/utils/dateAsIsoString';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { useToken } from '@/utils/token';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { SEADsfFundingStatus } from '../../data/useTakerHistory';
import ClearStatusChip from './ClearStatusChip';
import RequestCreditNoteModal from './RequestCreditNoteModal';
import { useMakerOrganizationName } from './utils';

const prefixUrl = import.meta.env.VITE_GATEWAY_URL;

const pendingClearCsvHref = ({
  takerId,
  marketId,
  clearId,
  token,
}: {
  takerId: number;
  marketId: string;
  clearId: number | null;
  token: string | null;
}) => `${prefixUrl}/api/c2fo/taker/${takerId}/market/${marketId}/pending-clear/${clearId}/csv?token=${token}`;

const archiveHref = ({
  takerId,
  marketId,
  payDate,
  format,
  token,
}: {
  takerId: number;
  marketId: string;
  payDate: string;
  format: 'csv' | 'pdf';
  token: string | null;
}) => `${prefixUrl}/api/c2fo/taker/${takerId}/market/${marketId}/archive/${payDate}/${format}?token=${token}`;

/**
 * Award and summary files for an Instant Pay row. A row can group several same-day offers, so each file is
 * downloaded once per statement id.
 */
const InstantPayDownloadMenu = ({
  makerOrganizationUuid,
  statementIds,
}: {
  makerOrganizationUuid: string;
  statementIds: string[];
}) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const { trackEnterpriseEvent } = useReporting();

  const handleDownload = async (type: InstantPayStatementType): Promise<void> => {
    trackEnterpriseEvent('history-csv-download::clicked');

    try {
      for (const statementId of statementIds) {
        await downloadInstantPayStatement({ makerOrganizationUuid, statementId, type });
      }
    } catch {
      showSnackbar({ message: t('core.downloadError') });
    }
  };

  return (
    <DownloadMenu
      name={t('maker.download')}
      disabled={statementIds.length === 0}
      groups={[
        { id: 'offer', label: t('taker.history.awardFile'), formats: [{ id: 'csv', label: t('maker.csv') }] },
        {
          id: 'offer-summary',
          label: t('instantPay.history.summaryFile'),
          formats: [{ id: 'csv', label: t('maker.csv') }],
        },
      ]}
      onSelect={(type) => void handleDownload(type)}
    />
  );
};

interface AwardDownloadMenuProps {
  row: HistoryTableDatum;
  onRequestCreditNote: (params: CreditNoteByClearParams) => void;
}

const AwardDownloadMenu = ({ row, onRequestCreditNote }: AwardDownloadMenuProps) => {
  const { t } = useTranslation();
  const { token } = useToken();
  const showSnackbar = useSnackbar();
  const { trackEnterpriseEvent } = useReporting();
  const [seaPendingClearCsvEnabled] = useFeature('enterprise-ui_enableSeaPendingClearCsv');
  const { takerId, marketId, payDate, id: clearId, makerOrganizationUuid } = row;

  // pending-clear csv download requires taker_market_clear.is_pending_clear = true, not just dsf pending status
  if (row.isPendingMarketClear) {
    const downloadThroughSea = seaPendingClearCsvEnabled && clearId != null;

    return (
      <DownloadMenu
        name={t('maker.download')}
        groups={[
          {
            id: 'award',
            label: t('taker.history.awardFile'),
            formats: [
              {
                id: 'csv',
                label: t('maker.csv'),
                href: downloadThroughSea ? undefined : pendingClearCsvHref({ takerId, marketId, clearId, token }),
              },
            ],
          },
        ]}
        onSelect={() => {
          trackEnterpriseEvent('history-csv-download::clicked');

          if (downloadThroughSea) {
            downloadSupplierMarketClearCsv({ clearId, makerOrganizationUuid }).catch(() =>
              showSnackbar({ message: t('core.downloadError') })
            );
          }
        }}
      />
    );
  }

  return (
    <DownloadMenu
      name={t('maker.download')}
      groups={[
        {
          id: 'award',
          label: t('taker.history.awardFile'),
          formats: [
            {
              id: 'pdf',
              label: t('core.pdf'),
              href: archiveHref({ takerId, marketId, payDate, format: 'pdf', token }),
            },
            {
              id: 'csv',
              label: t('maker.csv'),
              href: archiveHref({ takerId, marketId, payDate, format: 'csv', token }),
            },
            ...(row.hasCreditNote ? [{ id: 'credit-note' as const, label: t('core.notes') }] : []),
          ],
        },
      ]}
      onSelect={(_, format) => {
        if (format === 'pdf') {
          trackEnterpriseEvent('history-pdf-download::clicked');
        } else if (format === 'csv') {
          trackEnterpriseEvent('history-csv-download::clicked');
        } else {
          onRequestCreditNote({ takerId, marketId, payDate });
        }
      }}
    />
  );
};

const NoDataMessage = ({
  hasHistoryOutsideRange,
  onOpenFilters,
}: {
  hasHistoryOutsideRange?: boolean;
  onOpenFilters?: () => void;
}) => {
  const { t } = useTranslation();
  const icon = <img src={emptystatements} alt="emptystatements" className="h-28 w-28" />;
  const message = hasHistoryOutsideRange ? t('core.noRecordsInRangeDescription') : t('core.noRecordsDescription');
  const action =
    hasHistoryOutsideRange && onOpenFilters ? (
      <Button onClick={onOpenFilters} variant="tertiary">
        {t('core.adjustFilters')}
      </Button>
    ) : undefined;

  return <NoDataState action={action} className="py-8" icon={icon} title={t('core.noRecords')} message={message} />;
};

/**
 * The data required for each chart.
 */
export interface HistoryTableDatum {
  /**
   * The date of the pay period.
   */
  payDate: string;
  /**
   * The estimated date that payment will be deposited.
   */
  depositDate: string;
  /**
   * The total amount of cleared invoices for the pay period.
   */
  clearedInvoiceAmount: number;
  /**
   * The id of the taker.
   */
  takerId: number;
  /**
   * The id of the market.
   */
  marketId: string;
  /**
   * The uuid of the taker.
   */
  takerUuid: string;
  /**
   * The uuid of the market.
   */
  marketUuid: string;
  /**
   * The name of the maker organization.
   */
  makerOrganizationName?: string;
  /**
   * The uuid of the maker organization.
   */
  makerOrganizationUuid: string;
  /**
   * The name of the taker division.
   */
  takerDivisionName?: string;
  /**
   * The total amount of cleared invoices for the pay period.
   */
  clearedEarn: number;
  /**
   * The net amount after discount (clearedInvoiceAmount - clearedEarn).
   */
  clearedDiscountedAmount: number;
  /**
   * The cleared discount weighted avg of cleared invoices for the pay period.
   */
  clearedDiscountWeightedAvg: number;
  /**
   * The cleared dpe weighted avg of cleared invoices for the pay period.
   */
  clearedDpeWeightedAvg: number;
  /**
   * The cleared apr weighted avg of cleared invoices for the pay period.
   */
  clearedAprWeightedAvg: number;
  /**
   * The pending clear id
   */
  id: number | null;
  /**
   * The record includes a credit note
   */
  hasCreditNote?: boolean;
  /**
   * The dsf funding status of the clear. undefined for non-dsf clears
   */
  dsfFundingStatus?: SEADsfFundingStatus;
  /**
   * Whether the clear is pending (true for tmc.is_pending_clear OR dsfFundingStatus PENDING)
   */
  isPendingClear: boolean;
  /**
   * Whether taker_market_clear.is_pending_clear is true.
   */
  isPendingMarketClear: boolean;
  /**
   * Currency of the clear amounts.
   */
  currency: string;
  isInstantPay: boolean;
  /**
   * Offer statement ids of the row's Instant Pay clears. A row can group several same-day offers.
   */
  statementIds: string[];
}

export interface HistoryTableProps<T extends HistoryTableDatum> {
  data?: Array<T>;
  sort: string;
  setSort?: (sort: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageIndex?: number;
  pageSize: number;
  historyType: 'consolidated' | 'market';
  loading: boolean;
  hasHistoryOutsideRange?: boolean;
  onOpenFilters?: () => void;
}

const isFutureDate = (date: string) => isAfter(date, dateAsIsoString());

const HistoryTable = <T extends HistoryTableDatum>({
  data = [],
  pageSize,
  pageIndex = 0,
  sort: sortKey,
  onPageChange,
  onPageSizeChange,
  setSort,
  historyType,
  loading = false,
  hasHistoryOutsideRange = false,
  onOpenFilters,
}: HistoryTableProps<T>) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const { trackEnterpriseEvent } = useReporting();
  const { getMakerOrganizationName } = useMakerOrganizationName({ includeInactive: historyType === 'consolidated' });
  const { mutateAsync: requestCreditNote } = useCreditNoteByClear();
  const { asCurrency, asDateStringText, asNumber, asPercent } = useLocaleFormat();
  const [showRequestCreditNoteModal, setShowRequestCreditNoteModal] = useState(false);
  const { tableRef, showRightPinnedShadow } = usePinnedColumnShadow({ left: false });
  // Only show the funded by column on consolidated history
  const showFundedByColumn = historyType === 'consolidated';
  const hasMakerOrganizationName = historyType === 'consolidated';

  const sorting = useMemo<SortingState>(() => {
    const [, desc, key] = sortKey.match(/^(-)?(.*)$/) ?? [];
    return [{ id: key, desc: !!desc }];
  }, [sortKey]);

  const sort = useMemo<{ key: string; direction: TableSortDirection }>(() => {
    const col = sorting[0];
    return { key: col?.id ?? '', direction: col?.desc ? 'desc' : 'asc' };
  }, [sorting]);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const col = next[0];

      if (!col || !setSort) return;

      setSort(col.desc ? `-${col.id}` : col.id);
    },
    [sorting, setSort]
  );

  const handleRequestCreditNote = useCallback(
    async ({ takerId, marketId, payDate }: CreditNoteByClearParams) => {
      trackEnterpriseEvent('history-credit-note-request::clicked');

      await requestCreditNote(
        { takerId, marketId, payDate },
        {
          onSuccess: (response) => {
            if (response.status === 202) {
              setShowRequestCreditNoteModal(true);
            } else {
              showSnackbar({
                message: t('core.downloadError'),
              });
            }
          },
          onError: () => {
            showSnackbar({
              message: t('core.downloadError'),
            });
          },
        }
      );
    },
    [t, trackEnterpriseEvent, requestCreditNote, showSnackbar]
  );

  const columns = useMemo<ColumnDef<T>[]>(() => {
    const cols: ColumnDef<T>[] = [
      {
        id: 'awardedStatus',
        header: () => <span className="text-left">{t('taker.history.awardedStatus')}</span>,
        enableSorting: false,
        cell: ({ row }) => (
          <ClearStatusChip isPendingClear={row.original.isPendingClear} isInstantPay={row.original.isInstantPay} />
        ),
      },
    ];

    if (hasMakerOrganizationName) {
      cols.push({
        id: 'makerOrganizationName',
        accessorKey: 'makerOrganizationName',
        header: () => (
          <TableSortArrow accessorKey="makerOrganizationName" sort={sort} textRight={false}>
            {t('tpf.customer')}
          </TableSortArrow>
        ),
        enableSorting: true,
        cell: ({ row }) => {
          const { makerOrganizationName, marketIsInactive } = getMakerOrganizationName({
            makerOrganizationUuid: row.original.makerOrganizationUuid,
            marketUuid: row.original.marketUuid,
            takerUuid: row.original.takerUuid,
          });

          return (
            <div className="max-w-56 truncate">
              {makerOrganizationName ? (
                <>
                  <div className="truncate">
                    {marketIsInactive ? (
                      <div className="max-w-56 truncate font-medium">{makerOrganizationName}</div>
                    ) : (
                      <Link
                        className="max-w-56 truncate font-bold text-blue-500"
                        to={asEnterpriseLink(
                          `customers/${row.original.makerOrganizationUuid}/markets/${row.original.marketId}/division/${row.original.takerId}/invoices/eligible`
                        )}
                      >
                        {makerOrganizationName}
                      </Link>
                    )}
                  </div>
                  <div
                    className="flex max-w-56 items-center gap-1.5 truncate text-sm"
                    title={`${row.original.makerOrganizationName} - ${row.original.takerDivisionName}`}
                  >
                    <div>{row.original.makerOrganizationName}</div>
                    <div className="h-[3px] w-[3px] shrink-0 rounded-full bg-text-primary" />
                    <div className="truncate">{row.original.takerDivisionName}</div>
                  </div>
                </>
              ) : (
                <>--</>
              )}
            </div>
          );
        },
      });
    }

    cols.push(
      {
        id: 'depositDate',
        accessorKey: 'depositDate',
        header: () => (
          <TableSortArrow accessorKey="depositDate" sort={sort} textRight>
            {t('draftOffer.labels.estimatedDepositDate')}
          </TableSortArrow>
        ),
        enableSorting: true,
        cell: ({ row }) => {
          const displayDate = row.original.depositDate;
          const shouldShowFuturePaymentMessage = isFutureDate(displayDate);

          return (
            <span className="flex items-center justify-end gap-2">
              {shouldShowFuturePaymentMessage && (
                <Popover>
                  <Popover.Trigger>
                    <InfoCircleIcon className="fill-text-secondary" />
                  </Popover.Trigger>
                  <Popover.Content arrow side="left">
                    <div className="text-sm">{t('taker.history.futureEstimatedDepositDate')}</div>
                  </Popover.Content>
                </Popover>
              )}
              {asDateStringText({ date: displayDate, isUTC: false })}
            </span>
          );
        },
      },
      {
        id: 'clearedInvoiceAmount',
        accessorKey: 'clearedInvoiceAmount',
        header: () => (
          <TableSortArrow accessorKey="clearedInvoiceAmount" sort={sort} textRight>
            {t('taker.totalPaid')}
          </TableSortArrow>
        ),
        enableSorting: true,
        cell: ({ row }) => (
          <span className="flex justify-end">
            {asCurrency(row.original.clearedInvoiceAmount, row.original.currency)}
          </span>
        ),
      },
      {
        id: 'clearedEarn',
        accessorKey: 'clearedEarn',
        header: () => (
          <TableSortArrow accessorKey="clearedEarn" sort={sort} textRight>
            {t('taker.totalDiscount')}
          </TableSortArrow>
        ),
        enableSorting: true,
        cell: ({ row }) => (
          <span className="flex justify-end">{asCurrency(row.original.clearedEarn, row.original.currency)}</span>
        ),
      },
      {
        id: 'clearedDiscountedAmount',
        accessorKey: 'clearedDiscountedAmount',
        header: () => (
          <TableSortArrow accessorKey="clearedDiscountedAmount" sort={sort} textRight>
            {t('taker.transactionAmount')}
          </TableSortArrow>
        ),
        enableSorting: true,
        cell: ({ row }) => {
          const isDsfPaid = row.original.dsfFundingStatus === 'PAID';
          return (
            <span className="flex items-center justify-end gap-2">
              {isDsfPaid && (
                <Popover>
                  <Popover.Trigger>
                    <InfoCircleIcon className="visible fill-green-700" />
                  </Popover.Trigger>
                  <Popover.Content arrow side="bottom">
                    <div className="w-52 text-center text-sm">{t('taker.history.dsfTransactionAmountTooltip')}</div>
                  </Popover.Content>
                </Popover>
              )}
              {asCurrency(row.original.clearedDiscountedAmount, row.original.currency)}
            </span>
          );
        },
      },
      {
        id: 'clearedDiscountWeightedAvg',
        accessorKey: 'clearedDiscountWeightedAvg',
        header: () => (
          <TableSortArrow accessorKey="clearedDiscountWeightedAvg" sort={sort} textRight>
            {t('taker.averageDiscount')}
          </TableSortArrow>
        ),
        enableSorting: true,
        cell: ({ row }) => (
          <span className="flex justify-end">
            {asPercent({ value: row.original.clearedDiscountWeightedAvg / 100, decimals: 2 })}
          </span>
        ),
      },
      {
        id: 'clearedDpeWeightedAvg',
        accessorKey: 'clearedDpeWeightedAvg',
        header: () => (
          <TableSortArrow accessorKey="clearedDpeWeightedAvg" sort={sort} textRight>
            {t('core.averageDaysPaidEarly')}
          </TableSortArrow>
        ),
        enableSorting: true,
        cell: ({ row }) => (
          <span className="flex justify-end">
            {t('core.daysWithCount', {
              count: row.original.clearedDpeWeightedAvg,
              formattedCount: asNumber({ value: row.original.clearedDpeWeightedAvg, decimals: 0 }),
            })}
          </span>
        ),
      }
    );

    if (showFundedByColumn) {
      cols.push({
        id: 'fundedBy',
        header: () => t('taker.history.fundedBy'),
        enableSorting: false,
        cell: ({ row }) => {
          const isDsfClearWithStatus =
            row.original.dsfFundingStatus &&
            ['PAID', 'NOT_AVAILABLE', 'PENDING'].includes(row.original.dsfFundingStatus);

          const { makerOrganizationName } = getMakerOrganizationName({
            makerOrganizationUuid: row.original.makerOrganizationUuid,
            marketUuid: row.original.marketUuid,
            takerUuid: row.original.takerUuid,
          });

          if (row.original.isInstantPay) {
            return (
              <span className="flex gap-2">
                <Popover>
                  <Popover.Trigger>
                    <InfoCircleIcon className="visible fill-green-700" />
                  </Popover.Trigger>
                  <Popover.Content arrow side="bottom">
                    <div className="w-52 text-center text-sm">
                      {t('instantPay.history.fundedByTooltip', { fundingPartner: INSTANT_PAY_FUNDING_PARTNER })}
                    </div>
                  </Popover.Content>
                </Popover>
                {INSTANT_PAY_FUNDING_PARTNER}
              </span>
            );
          }

          if (isDsfClearWithStatus) {
            return (
              <span className="flex gap-2">
                <Popover>
                  <Popover.Trigger>
                    <InfoCircleIcon className="visible fill-green-700" />
                  </Popover.Trigger>
                  <Popover.Content arrow side="bottom">
                    <div className="w-52 text-center text-sm">{t('taker.history.fundedByBankPartnerTooltip')}</div>
                  </Popover.Content>
                </Popover>
                {t('taker.history.bankPartner')}
              </span>
            );
          }

          return (
            <span className="flex gap-2">
              <InfoCircleIcon className="invisible fill-green-700" />
              {makerOrganizationName}
            </span>
          );
        },
      });
    }

    cols.push({
      id: 'downloads',
      header: () => <span className="flex justify-center">{t('maker.download')}</span>,
      enableSorting: false,
      size: 120,
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          {/* Instant Pay rows always use the statement menu: the legacy archive links can't serve their clears */}
          {row.original.isInstantPay ? (
            <InstantPayDownloadMenu
              makerOrganizationUuid={row.original.makerOrganizationUuid}
              statementIds={row.original.statementIds}
            />
          ) : (
            <AwardDownloadMenu row={row.original} onRequestCreditNote={handleRequestCreditNote} />
          )}
        </div>
      ),
    });

    return cols;
  }, [
    t,
    sort,
    hasMakerOrganizationName,
    showFundedByColumn,
    asCurrency,
    asDateStringText,
    asNumber,
    asPercent,
    getMakerOrganizationName,
    handleRequestCreditNote,
  ]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    enableSortingRemoval: false,
    sortDescFirst: true,
    state: { sorting },
    onSortingChange: handleSortingChange,
    initialState: {
      columnPinning: {
        right: ['downloads'],
      },
    },
  });

  const colSpan = table.getVisibleLeafColumns().length;

  return (
    <QueryBoundaries LoadingComponent={() => <Skeleton />}>
      <RequestCreditNoteModal open={showRequestCreditNoteModal} onClose={() => setShowRequestCreditNoteModal(false)} />
      <div ref={tableRef} className="relative w-full overflow-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    isSorted={!!header.column.getIsSorted()}
                    className={cn({
                      'cursor-pointer transition-all duration-200 hover:bg-gray-100/50': header.column.getCanSort(),
                      'bg-gray-100 hover:bg-gray-100': header.column.getIsSorted(),
                      'bg-canvas': header.column.getIsPinned() === 'right',
                      'pl-10': header.column.id === 'fundedBy',
                      [rightShadowClassNames]: header.column.getIsFirstColumn('right') && showRightPinnedShadow,
                    })}
                    onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                    style={getCommonPinningStyles(header.column)}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody loading={loading}>
            {!data.length && (
              <TableDisplay colSpan={colSpan}>
                <NoDataMessage hasHistoryOutsideRange={hasHistoryOutsideRange} onOpenFilters={onOpenFilters} />
              </TableDisplay>
            )}
            {table.getRowModel().rows.map((row, i) => (
              <TableRow key={`${row.original.marketId}-${i}`}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn({
                      'bg-white': cell.column.getIsPinned() === 'right',
                      [rightShadowClassNames]: cell.column.getIsFirstColumn('right') && showRightPinnedShadow,
                    })}
                    style={getCommonPinningStyles(cell.column)}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        className="mt-auto"
        pageSize={pageSize}
        onPageSizeChange={(e) => onPageSizeChange(+e.target.value)}
        onNextChange={() => onPageChange(pageIndex + 1)}
        onPrevChange={() => onPageChange(pageIndex - 1)}
        disablePrev={pageIndex === 1}
        disableNext={data.length < pageSize}
      />
    </QueryBoundaries>
  );
};

export default HistoryTable;
